import { DateUtil } from "@/utilities/date-utilities";
import { gql } from "@apollo/client";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Button from "components/common/button/Button";
import Datalist, { DatalistPropsType } from "components/common/datalist/Datalist";
import DateView from "components/common/views/DateView";
import MoneyView from "components/common/views/MoneyView";
import { uuid } from "config/app-constants";
import { graph } from "config/axios-context";
import { useState } from "react";
import { SortEnumType, WorkerDatalistQuery, WorkerDatalistQueryVariables, WorkerFilterInput, WorkerSortInput } from "types/graphql";
import { appendFilters } from "utilities/datalist/datalist-filter-utilities";
import useAppContext from "utilities/hooks/useAppContext";

type DataItem = NonNullable<WorkerDatalistQuery['workers']>['items'][0]
type WorkerDatalistProps = {
    fixedFilters?: WorkerFilterInput,
    name: string
}


const query = gql`
query WorkerDatalist($where: WorkerFilterInput, $skip: Int, $take: Int, $order:[WorkerSortInput!]) {
    workers {
      count(where:$where)
      priceSum(where: $where)
      items(where:$where, skip:$skip, take:$take, order:$order) {
        work {
          name,
          startDate,
          endDate,
          season {
              name
            },
          category
        },
        id
        price
      }
    }    
  }
`;

const WorkerDatalist = (props: WorkerDatalistProps) => {
    const context = useAppContext();
    const [priceSum, setPriceSum] = useState<number>();

    const cofig: DatalistPropsType<DataItem, WorkerFilterInput, WorkerSortInput> = {
        itemId: i => i.id,
        name: props.name + "WorkerDatalist",
        dataSource: {
            request: async (params) => {
                let variables: WorkerDatalistQueryVariables = {
                    order: params.sort,
                    take: params.take,
                    skip: params.skip,
                    where: params.filters
                }

                if (props.fixedFilters) {
                    variables.where = appendFilters(variables.where, props.fixedFilters);
                }

                if (params.searchText) {
                    var search: WorkerFilterInput = {
                        or: [
                            {
                                person: {
                                    name: {
                                        contains: params.searchText
                                    }
                                }
                            },
                            {
                                person: {
                                    contactInfo: {
                                        some: {
                                            value: {
                                                contains: params.searchText.replace(/\s/g, '')
                                            }
                                        }
                                    }
                                }
                            },
                            {
                                work: {
                                    name: {
                                        contains: params.searchText
                                    }
                                }
                            }
                        ]
                    }
                }

                const response = await graph<WorkerDatalistQuery, WorkerDatalistQueryVariables>({
                    query,
                    variables
                });

                if (response.errors) {
                    throw response.errors;
                }

                setPriceSum(response.data?.workers?.priceSum);
                return response.data?.workers!;
            },
            sort: [
                {
                    id: uuid(),
                    direction: SortEnumType.Desc,
                    name: 'startDate'
                }
            ]
        },
        table: {
            columns: [
                {
                    name: 'startDate',
                    title: 'fields.date.shortName',
                    resolve: i => <DateView date={i.work.startDate} endDate={i.work.endDate} />,
                    sort: {
                        resolve: d => ({
                            work: {
                                startDate: d
                            }
                        }),
                    },
                    filter: {
                        type: 'date',
                        title: 'fields.startDate.name',
                        resolve: i => ({
                            work: {
                                startDate: {
                                    [`${i.condition}`]: i.value ? DateUtil.toIsoDate(i.value) : null
                                }
                            }
                        })
                    },
                    card: {
                        group: 'name'
                    }
                },
                {
                    name: 'name',
                    resolve: i => i.work.name,
                    sort: {
                        resolve: d => ({
                            work: {
                                name: d
                            }
                        }),
                    },
                    filter: {
                        type: 'string',
                        resolve: i => ({
                            work: {
                                name: {
                                    [`${i.condition}`]: i.value
                                }
                            }
                        })
                    },
                    card: {
                        group: 'name',
                    }
                },
                {
                    name: 'endDate',
                    hide: true,
                    sort: {
                        resolve: d => ({
                            work: {
                                endDate: d
                            }
                        }),
                    },
                    filter: {
                        type: 'date',
                        title: 'fields.endDate.name',
                        resolve: i => ({
                            work: {
                                endDate: {
                                    [`${i.condition}`]: i.value ? DateUtil.toIsoDate(i.value) : null
                                }
                            }
                        })
                    }
                },
                {
                    name: 'price',
                    sort: true,
                    filter: {
                        type: 'number'
                    },
                    resolve: i => <MoneyView value={i.price} />
                },
                {
                    name: 'category',
                    sort: {
                        resolve: d => ({
                            work: {
                                category: d
                            }
                        }),
                    },
                    resolve: i => context.t('entities.work.categories.' + i.work.category),
                    card: {
                        group: 'type',
                        textSize: 'text-sm'
                    }
                },
                {
                    name: 'season',
                    sort: true,
                    resolve: i => i.work.season.name,
                    card: {
                        group: 'type',
                        textSize: 'text-sm'
                    }
                }
            ],
            render: {

                card(item, index) {
                    return <div key={item.id} className={`w-full border px-4 py-2 mb-4 hover:border-sunrise-300 theme-border flex`} >
                        <div className="w-full">
                            <div>
                                <span><DateView date={item.work.startDate} endDate={item.work.endDate} /></span>
                            </div>
                            <div>
                                <i>{item.work.name}</i>
                            </div>
                            <div className="text-sm">
                                <span>{item.work.season.name} - {item.work.category}</span>
                            </div>
                        </div>
                        <div className="mt-auto pl-4 w-max whitespace-nowrap">
                            <div>
                                <b><MoneyView value={item.price} /></b>
                            </div>
                            {/* <div> TODO: add this
                            <Button theme="link primary" title="button.open">
                                <FontAwesomeIcon icon={faEye} />
                            </Button>
                        </div> */}
                        </div>
                    </div>
                },
                footer: {
                    table: {
                        columns: [
                            {
                                name: 'one',
                                colSpan: 2
                            },
                            {
                                name: 'price',
                                value: <MoneyView value={priceSum} />
                            },
                            {
                                name: 'tow',
                                colSpan: 2
                            }
                        ]
                    }
                }
            }
        }
    }

    return <Datalist {...cofig} />;
}

export default WorkerDatalist;
